import { Danger } from "iconsax-reactjs";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DIAGNOSTIC_STEP_AUDIO_VALUE, DIAGNOSTIC_STEP_VIDEO_KEY, DIAGNOSTIC_STEP_VIDEO_VALUE } from "@/constants/diagnostics";
import { useCameraTest } from "@/hooks/useCameraTest";
import useDevices from "@/hooks/useDevices";
import { useIsSM } from "@/hooks/useIsSM";
import { selectDiagnosticsStep } from "@/store/features/diagnostics/selectors";
import { setDiagnosticsStep } from "@/store/features/diagnostics/slice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { Button, Select } from "@/ui";
import DiagnosticsSection from "../DiagnosticsSection";

const Video = () => {
  const { t } = useTranslation();

  const isSM = useIsSM();

  const dispatch = useAppDispatch();
  const step = useAppSelector(selectDiagnosticsStep);

  const { videoInputDevices } = useDevices();
  const { videoElementRef, startVideoTest, stopVideoTest, videoTest, videoTestError } = useCameraTest();

  const prevVideoDeviceID = useRef('');
  const [videoInputDeviceID, setVideoInputDeviceID] = useState('');

  const setDevice = (deviceID: string) => {
    setVideoInputDeviceID(deviceID);
  };

  useEffect(() => {
    // Stop the test when we are not on the CameraTest pane and there is an active test
    if (step !== DIAGNOSTIC_STEP_VIDEO_VALUE && videoTest) {
      stopVideoTest();
      prevVideoDeviceID.current = '';
    }
  }, [step, stopVideoTest, videoTest]);

  useEffect(() => {
    // Start the test when we are on the CameraTest pane and when the videoInputDeviceID changes
    if (step === DIAGNOSTIC_STEP_VIDEO_VALUE) {
      const newDeviceSelected = prevVideoDeviceID.current !== videoInputDeviceID;
      prevVideoDeviceID.current = videoInputDeviceID;

      if (videoInputDeviceID && newDeviceSelected) {
        startVideoTest(videoInputDeviceID);
      }

      if (videoTestError) {
        stopVideoTest();
      }
    }
  }, [startVideoTest, step, stopVideoTest, videoInputDeviceID, videoTestError]);

  useEffect(() => {
    // If no device is select, set the first available device as the active device.
    const hasSelectedDevice = videoInputDevices.some((device) => device.deviceId === videoInputDeviceID);
    if (videoInputDevices.length && !hasSelectedDevice) {
      setVideoInputDeviceID(videoInputDevices[0].deviceId);
    }
  }, [videoInputDevices, videoInputDeviceID]);

  const onClickNext = () => {
    dispatch(setDiagnosticsStep(DIAGNOSTIC_STEP_AUDIO_VALUE));
  };

  const preview = (
    <div className="section-frame">
      {videoTestError ? (
        <h4>
          <Danger
            size="18"
            color="var(--color-danger)"
            variant="Bold"
          />
          {t('unable_to_connect')}
        </h4>
      ) : (
        <h4>{t('video_preview')}</h4>
      )}
      <video autoPlay playsInline ref={videoElementRef} />
      <Select
        label={t('select_camera')}
        value={videoInputDeviceID}
        options={videoInputDevices.map((device) => ({ label: device.label, value: device.deviceId }))}
        onChange={setDevice}
      />
    </div>
  );

  return (
    <DiagnosticsSection id={DIAGNOSTIC_STEP_VIDEO_KEY}>
      <div className="section-wrapper">
        <div className="section-content">
          <h2>{t('check_your_video')}</h2>
          <p>{t('check_your_video_description_1')}</p>
          {isSM && preview}
          <p>
            <b>{t('check_your_video_description_2')}</b>
          </p>
          <div className="section-buttons">
            <Button onClick={onClickNext}>{t('yes')}</Button>
            <Button variant="link" onClick={onClickNext}>{t('skip_for_now')}</Button>
          </div>
        </div>
        {!isSM && preview}
      </div>
    </DiagnosticsSection>
  );
};

export default Video;